// Small shared primitives. The mobile app has AppButton/Pill/Screen as React
// Native components; these are their web equivalents so ported screens keep
// the same structure and the two clients stay visually consistent.
import React, { useCallback, useEffect, useRef, useState } from 'react';

// `block` opts into a full-width button. Buttons size to their label by
// default, which is the web idiom - full width is reserved for the cases
// where the button IS the page, like the sign-in card.
export function Button({ children, secondary, danger, compact, block, ...props }) {
  const cls = ['btn', secondary && 'secondary', danger && 'danger', compact && 'compact', block && 'block'].filter(Boolean).join(' ');
  return <button type="button" className={cls} {...props}>{children}</button>;
}

// complete/failed/queued are the states the AI workers actually emit; anything
// else falls back to neutral rather than guessing at a colour.
function badgeTone(status) {
  const value = String(status || '').toLowerCase();
  if (/complete|success|done|indexed/.test(value)) return 'ok';
  if (/fail|error|rejected/.test(value)) return 'bad';
  if (/queue|running|pending|progress|not_run/.test(value)) return 'warn';
  return '';
}

export function Card({ title, subtitle, badge, hint, children, id }) {
  return (
    <section className="card" id={id}>
      {(title || badge) && (
        <div className="card-head">
          <div style={{ minWidth: 0 }}>
            {title && <div className="card-title">{title}</div>}
            {subtitle && <div className="card-subtitle">{subtitle}</div>}
          </div>
          {badge && <span className={`badge ${badgeTone(badge)}`.trim()}>{badge}</span>}
        </div>
      )}
      {hint && <p className="card-hint">{hint}</p>}
      {children}
    </section>
  );
}

// A native <select> rather than a custom dropdown: it is keyboard accessible,
// works on touch, and needs no open/close state. This is the web counterpart
// of the mobile PickerModal.
export function Select({ label, hint, value, onChange, options = [], placeholder, disabled }) {
  return (
    <div className="field">
      {label && <label className="field-label">{label}</label>}
      <select
        className="input"
        value={value ?? ''}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((o) => (
          <option key={String(o.id)} value={o.id}>
            {o.sublabel ? `${o.label} — ${o.sublabel}` : o.label}
          </option>
        ))}
      </select>
      {hint && <p className="card-hint" style={{ margin: '6px 0 0' }}>{hint}</p>}
    </div>
  );
}

export function Field({ label, hint, children }) {
  return (
    <div className="field">
      {label && <label className="field-label">{label}</label>}
      {children}
      {hint && <p className="card-hint" style={{ margin: '6px 0 0' }}>{hint}</p>}
    </div>
  );
}

export function PillGroup({ items = [], value, onChange, empty }) {
  if (!items.length) return <p className="muted">{empty}</p>;
  return (
    <div className="pill-wrap">
      {items.map((item) => {
        const id = typeof item === 'string' ? item : item.id;
        const label = typeof item === 'string' ? item : item.label;
        return (
          <button
            key={String(id)}
            type="button"
            className={`pill${value === id ? ' active' : ''}`}
            onClick={() => onChange(id)}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

export function Chips({ items = [], onRemove }) {
  if (!items.length) return null;
  return (
    <div className="pill-wrap" style={{ marginBottom: 10 }}>
      {items.map((item) => (
        <span className="chip" key={item.id}>
          {item.label}
          {onRemove && <button type="button" onClick={() => onRemove(item.id)} aria-label={`Remove ${item.label}`}>×</button>}
        </span>
      ))}
    </div>
  );
}

// Files are identified by name + size + last-modified rather than name alone:
// two photos from different folders regularly share a name, and dropping the
// same file twice should be a no-op rather than a duplicate upload.
function fileKey(file) {
  return `${file.name}|${file.size}|${file.lastModified}`;
}

function mergeFiles(existing, incoming) {
  const seen = new Set(existing.map(fileKey));
  const added = [];
  for (const file of incoming) {
    const key = fileKey(file);
    if (seen.has(key)) continue;
    seen.add(key);
    added.push(file);
  }
  return existing.concat(added);
}

// Drop target for uploads, matching the Hub's behaviour: drop anywhere on the
// zone, or click it to browse. Dropped files are APPENDED, so several drops
// build one batch instead of each replacing the last.
export function FileDrop({ files = [], onChange, disabled }) {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  // dragenter/dragleave also fire when the pointer crosses a CHILD element, so
  // a plain boolean flickers as you move across the zone's own text. Counting
  // enters against leaves is what makes the highlight stable.
  const depth = useRef(0);

  // Without this, a drop that misses the zone makes the browser navigate away
  // from the app to display the file - losing everything the user had picked.
  // The Hub guards the window for the same reason.
  useEffect(() => {
    const swallow = (event) => {
      if (event.dataTransfer?.types?.includes('Files')) event.preventDefault();
    };
    window.addEventListener('dragover', swallow);
    window.addEventListener('drop', swallow);
    return () => {
      window.removeEventListener('dragover', swallow);
      window.removeEventListener('drop', swallow);
    };
  }, []);

  const add = useCallback((list) => {
    const incoming = Array.from(list || []);
    if (incoming.length) onChange(mergeFiles(files, incoming));
  }, [files, onChange]);

  const hasFiles = (event) => !!event.dataTransfer?.types?.includes('Files');

  return (
    <>
      <div
        className={`dropzone${over ? ' over' : ''}${disabled ? ' disabled' : ''}`}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label="Add files by dropping them here or browsing"
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inputRef.current?.click(); }
        }}
        onDragEnter={(e) => { if (!disabled && hasFiles(e)) { e.preventDefault(); depth.current += 1; setOver(true); } }}
        onDragOver={(e) => { if (!disabled && hasFiles(e)) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; } }}
        onDragLeave={() => { depth.current = Math.max(0, depth.current - 1); if (!depth.current) setOver(false); }}
        onDrop={(e) => {
          if (disabled) return;
          e.preventDefault();
          depth.current = 0;
          setOver(false);
          add(e.dataTransfer?.files);
        }}
      >
        <span className="dropzone-icon" aria-hidden="true">⬆</span>
        <span className="dropzone-title">{over ? 'Drop to add' : 'Drag files here'}</span>
        <span className="dropzone-hint">or click to browse — you can add several at once</span>
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          disabled={disabled}
          onChange={(e) => { add(e.target.files); e.target.value = ''; }}
        />
      </div>

      {files.length > 0 && (
        <ul className="file-list">
          {files.map((file) => (
            <li key={fileKey(file)}>
              <span className="file-name">{file.name}</span>
              <span className="file-size">{formatBytes(file.size)}</span>
              <button
                type="button"
                className="file-remove"
                aria-label={`Remove ${file.name}`}
                disabled={disabled}
                onClick={() => onChange(files.filter((f) => fileKey(f) !== fileKey(file)))}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function Loading({ text = 'Loading…', rows = 0 }) {
  if (rows > 0) {
    return (
      <div aria-busy="true" aria-label={text}>
        {Array.from({ length: rows }, (_, i) => <div key={i} className="skeleton skeleton-row" />)}
      </div>
    );
  }
  return <p className="muted"><span className="spinner" /> {text}</p>;
}

export function Empty({ icon = '📭', title, children }) {
  return (
    <div className="empty">
      <span className="empty-icon">{icon}</span>
      {title && <div className="empty-title">{title}</div>}
      {children && <div>{children}</div>}
    </div>
  );
}

export function ErrorText({ children }) {
  return children ? <p className="error">{children}</p> : null;
}

// `error` is a machine-readable code ("report_download_request_failed");
// `details` is the actual message. Showing the code first meant a 500 said
// nothing about what went wrong, when the server had sent the reason all
// along. Prefer details, and keep the code alongside it so a support request
// still names the endpoint that failed.
export function errorMessage(err) {
  const data = err?.response?.data;
  const details = data?.details ? String(data.details) : '';
  const code = data?.error ? String(data.error) : '';
  if (details && code) return `${details} (${code})`;
  return String(details || code || err?.message || err);
}
